import { GrpcChatAdapter } from './GrpcChatAdapter';
import { DeliveryRouteChatAdapter } from './DeliveryRouteChatAdapter';

/**
 * ChatFactory - Factory pattern para crear instancias de adaptadores de chat
 *
 * Permite cambiar entre diferentes implementaciones de chat (Firebase, gRPC)
 * mediante detección automática de versión del driver o variable de entorno
 */
export class ChatFactory {
  /**
   * Versión mínima del driver requerida para usar gRPC
   * Drivers con versión >= 5.40.22 usan gRPC
   * Drivers con versión < 5.40.22 usan Firebase
   */
  static MIN_GRPC_VERSION = '5.40.22';

  /**
   * Compara dos versiones en formato semver (ej: "5.40.22")
   * @param {string} version1 - Primera versión a comparar
   * @param {string} version2 - Segunda versión a comparar
   * @returns {number} -1 si version1 < version2, 0 si son iguales, 1 si version1 > version2
   */
  static compareVersions(version1, version2) {
    if (!version1 || !version2) return -1;

    const v1Parts = version1.toString().split('.').map(Number);
    const v2Parts = version2.toString().split('.').map(Number);

    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const v1Part = v1Parts[i] || 0;
      const v2Part = v2Parts[i] || 0;

      if (v1Part > v2Part) return 1;
      if (v1Part < v2Part) return -1;
    }

    return 0;
  }

  /**
   * Determina si debe usar gRPC basándose en la versión del driver
   * @param {string} driverVersion - Versión de la app del driver (ej: "5.40.22")
   * @returns {boolean} true si debe usar gRPC, false si debe usar Firebase
   */
  static shouldUseGrpc(driverVersion) {
    // Si hay variable de entorno, tiene prioridad (override manual)
    const envProvider = process.env.REACT_APP_CHAT_PROVIDER;
    if (envProvider) {
      return envProvider.toLowerCase() === 'grpc';
    }

    // Si no hay versión del driver, usar Firebase por defecto (seguro)
    if (!driverVersion) {
      return false;
    }

    // Comparar versión del driver con la versión mínima requerida
    const comparison = this.compareVersions(driverVersion, this.MIN_GRPC_VERSION);
    const useGrpc = comparison >= 0; // >= 5.40.22

    return useGrpc;
  }

  /**
   * Crea una instancia del adaptador de chat configurado
   *
   * - Con `deliveryRouteId` y `prePackageId`: chat entre el conductor de una ruta de
   *   entrega y la compañía dueña de ESE prepaquete (DeliveryRouteChatAdapter). Grano por
   *   paquete, no por compañía (specs/007-chat-por-paquete/): dos paquetes de la misma
   *   compañía en la misma ruta son dos adaptadores distintos.
   * - En cualquier otro caso: chat de booking (GrpcChatAdapter).
   *
   * @param {string|{driverVersion?: string, deliveryRouteId?: string, prePackageId?: string}} arg
   *   Versión del driver (forma antigua, posicional) u objeto de opciones
   * @returns {ChatAdapter} Instancia del adaptador de chat
   */
  static createChatAdapter(arg = null) {
    const { deliveryRouteId, prePackageId } = arg && typeof arg === 'object' ? arg : {};

    if (deliveryRouteId && prePackageId) {
      return new DeliveryRouteChatAdapter({ deliveryRouteId, prePackageId });
    }

    // Siempre usar GrpcChatAdapter para el chat de booking
    // - Lee de gRPC primero, fallback a Firebase si está vacío
    // - Escribe siempre a gRPC
    // - Resuelve el caso donde un driver baja de versión pero ya tiene historial en gRPC
    return new GrpcChatAdapter();
  }

  /**
   * Obtiene el nombre del proveedor de chat actual
   * @param {string} driverVersion - Versión de la app del driver (opcional)
   * @returns {string} Nombre del proveedor ('firebase' o 'grpc')
   */
  static getCurrentProvider(driverVersion = null) {
    return this.shouldUseGrpc(driverVersion) ? 'grpc' : 'firebase';
  }

  /**
   * Verifica si el proveedor de chat es Firebase
   * @param {string} driverVersion - Versión de la app del driver (opcional)
   * @returns {boolean} true si el proveedor es Firebase
   */
  static isFirebase(driverVersion = null) {
    return this.getCurrentProvider(driverVersion) === 'firebase';
  }

  /**
   * Verifica si el proveedor de chat es gRPC
   * @param {string} driverVersion - Versión de la app del driver (opcional)
   * @returns {boolean} true si el proveedor es gRPC
   */
  static isGrpc(driverVersion = null) {
    // Siempre true porque siempre usamos GrpcChatAdapter
    return true;
  }
}

export default ChatFactory;
